import type { CSSProperties, FC, RefAttributes } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon';
import { Spin as ANTDSpin } from 'antd';
import cn from 'classnames';
import { useAppSelector } from 'services/redux/configureStore';

import styles from './Spin.module.css';

type SpinMode = 'visible' | 'default';

interface SpinProps {
  styleWrapper?: CSSProperties;
  classNameWrapper?: string;
  classNameSpinner?: string;
  mode?: SpinMode;
  size?: 'xs' | 's' | 'l';
  overlay?: boolean;
  fixed?: boolean;
  onlyOverly?: boolean;
  IconComponent?: FC<Omit<AntdIconProps, 'ref'> & RefAttributes<HTMLSpanElement>>;
  isLoading: boolean;
}

const Spin = ({
  styleWrapper,
  classNameWrapper,
  classNameSpinner,
  mode = 'default',
  size = 'l',
  overlay = true,
  onlyOverly = false,
  fixed = true,
  isLoading,
  IconComponent,
}: SpinProps) => {
  const isDarkMode = useAppSelector((state) => state.theme.isDarkMode);

  const mapStylesMode: Record<SpinMode, string> = {
    default: 'default-mode',
    visible: 'visible-mode',
  };

  return (
    <div
      style={styleWrapper}
      className={cn(
        styles.wrapper,
        styles[mapStylesMode[mode]],
        {
          [styles.inactive]: !isLoading,
          [styles.active]: isLoading,
          [styles.light]: !isDarkMode,
          [styles.dark]: isDarkMode,
          [styles.overlay]: overlay,
          [styles.fixed]: fixed,
        },
        classNameWrapper
      )}
    >
      <ANTDSpin
        className={cn(styles.spinner, classNameSpinner, styles[size], {
          [styles.visible]: mode === 'visible',
        })}
        spinning={isLoading}
        indicator={
          onlyOverly ? (
            <></>
          ) : IconComponent ? (
            <IconComponent spin={isLoading} />
          ) : (
            <LoadingOutlined spin={isLoading} />
          )
        }
      />
    </div>
  );
};

export default Spin;
